import { useEffect } from 'react';
import { LicenseInfo } from '@mui/x-license-pro';
import { captureAdAttributionFromUrl } from 'src/utils/adAttribution';
// i18n
import './locales/i18n';

// highlight
import './utils/highlight';

// fonts
import '@fontsource-variable/public-sans';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';

// map
import 'maplibre-gl/dist/maplibre-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

// facebook
import { FacebookProvider } from 'react-facebook';

// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';

// redux
import * as Sentry from "@sentry/react";
import ReduxProvider from './redux/reduxProvider';

// contexts
import { SettingsProvider, SettingsDrawer } from './components/settings';
import { AuthProvider } from './contexts/JWTContext';

// routes
import Router from './routes';

// theme
import ThemeProvider from './theme';

// components
import { MotionLazy } from './components/animate/motion-lazy';
import SnackbarProvider from './components/snackbar/snackbar-provider';
import { ErrorHandler } from './components';
import './App.css';
import { defaultSettings } from './config';
import LiveNotificationHandler from './components/NotificationHandler/LiveNotificationHandler';
import NotificationProvider from './sections/notifications/NotificationProvider';
import VersionUpdateNotifier from './components/VersionUpdateNotifier';
import AppErrorBoundary from './AppErrorBoundary';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

// ----------------------------------------------------------------------

export default function App() {

    useEffect(() => {
        const domain = window.location.hostname;

        if (domain === 'dental-sem2.hupp.in' || domain === 'dental-sem.hupp.in') {
            const meta = document.createElement('meta');
            meta.name = 'robots';
            meta.content = 'noindex';
            document.getElementsByTagName('head')[0].appendChild(meta);
        }

        // Capture gclid + UTM from the landing-page URL once at app boot.
        // Safe to call unconditionally — no-op when no ad parameters are
        // present. First-attribution-wins, so a later navigation to a
        // non-ads URL won't clobber the original click. Used by the
        // lead-creation thunks to tie CRM leads back to Google Ads
        // campaigns for cost-per-lead reporting and offline conversion
        // uploads (see src/utils/adAttribution.js + backend gclid field).
        captureAdAttributionFromUrl();
    }, []);

    // Service Worker registration is now handled by useVersionCheck hook
    // to enable automatic version update detection while preserving FCM functionality

    return (
        <Sentry.ErrorBoundary
            fallback={<AppErrorBoundary />}
            showDialog
            onError={(error) => {
                const msg = error?.message || '';
                if (msg.includes('Invalid hook call') || msg.includes('Minified React error #321')) {
                    const reloadKey = 'hook_error_reload';
                    if (!sessionStorage.getItem(reloadKey)) {
                        sessionStorage.setItem(reloadKey, '1');
                        window.location.reload();
                    }
                }
            }}
        >
            <AuthProvider>
                <ReduxProvider>
                    <FacebookProvider appId="573147954687607">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <SettingsProvider defaultSettings={defaultSettings} >
                                <ThemeProvider>
                                    <MotionLazy>
                                        <SnackbarProvider>
                                            <NotificationProvider>
                                                <SettingsDrawer />
                                                <LiveNotificationHandler />
                                                <VersionUpdateNotifier />
                                                <ErrorHandler />
                                                <Router />
                                            </NotificationProvider>
                                        </SnackbarProvider>
                                    </MotionLazy>
                                </ThemeProvider>
                            </SettingsProvider>
                        </LocalizationProvider>
                    </FacebookProvider>
                </ReduxProvider>
            </AuthProvider>
        </Sentry.ErrorBoundary>
    );
}
